import { useState } from 'react';
import { Button, Modal, Input, Typography } from 'antd';
import { Link as LinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { App as AntApp } from 'antd';
import { useWalletConnect } from '@/hooks/useWalletConnect';
import type { WcVaultBinding } from '@/types/walletconnect';

const { Text } = Typography;

/**
 * Entry point for connecting a vault to a dApp. The caller supplies the vault
 * binding (org, vault, chain, EVM address); the user pastes a `wc:` URI copied
 * from the dApp's "WalletConnect" dialog.
 */
export function ConnectDappButton({ binding }: { binding: WcVaultBinding }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const wc = useWalletConnect();
  const [open, setOpen] = useState(false);
  const [uri, setUri] = useState('');
  const [pairing, setPairing] = useState(false);

  if (!wc) return null;

  const handlePair = async () => {
    const value = uri.trim();
    if (!value.startsWith('wc:')) {
      void message.error(t('walletconnect.connect.invalid_uri'));
      return;
    }
    setPairing(true);
    try {
      await wc.connect(value, binding);
      setUri('');
      setOpen(false);
    } catch (err) {
      void message.error(
        err instanceof Error ? err.message : t('walletconnect.errors.pair_failed')
      );
    } finally {
      setPairing(false);
    }
  };

  return (
    <>
      <Button
        icon={<LinkIcon />}
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('walletconnect.connect.button')}
      </Button>
      <Modal
        open={open}
        title={t('walletconnect.connect.title')}
        okText={t('walletconnect.connect.pair')}
        onOk={() => void handlePair()}
        onCancel={() => {
          setOpen(false);
        }}
        confirmLoading={pairing}
        destroyOnHidden
      >
        <Text type="secondary">{t('walletconnect.connect.help')}</Text>
        <Input.TextArea
          value={uri}
          onChange={(e) => {
            setUri(e.target.value);
          }}
          placeholder="wc:…"
          rows={3}
          style={{ marginTop: 12, fontFamily: 'monospace' }}
          autoFocus
        />
      </Modal>
    </>
  );
}
